// src/hooks/useBenderSession.js
import { useState, useEffect, useRef } from 'react';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import {
  doc, setDoc, deleteDoc, serverTimestamp, collection, query,
  onSnapshot, orderBy, limit
} from 'firebase/firestore';
import { calculateSmartScore, getWeekId } from '../utils/scoring';
import { resilientSnapshot } from '../utils/resilientSnapshot';
import { fireNotification } from '../native/notify';
import { getTeamCountry } from '../utils/country';

const useBenderSession = (db, auth, APP_ID, stats, setStats, showChat, onNotify) => {
  // --- STATE ---
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState(localStorage.getItem('bender_email'));
  const [username, setUsername] = useState(localStorage.getItem('bender_username'));
  const [squadId, setSquadId] = useState(null);
  const [squadMembers, setSquadMembers] = useState([]);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Live ref to the latest stats so the cloud-sync interval below does NOT need
  // `stats` in its dep array. `stats` mutates every second (hydration decay), so
  // depending on it would tear down + recreate the 15s interval every tick and it
  // would never fire — leaving the leaderboard/user-doc permanently at 0.
  const statsRef = useRef(stats);
  const prevSquadRef = useRef(null);
  useEffect(() => { statsRef.current = stats; }, [stats]);

  // --- 1. AUTHENTICATION ---
  useEffect(() => {
    signInAnonymously(auth).catch(console.error);
    return onAuthStateChanged(auth, setUser);
  }, [auth]);

  // --- 2. DATA LOAD (Initial Fetch) ---
  useEffect(() => {
    if (!user) return;
    
    // Listen to my own user document
    // includeMetadataChanges so we also hear the snapshot that arrives when a
    // local write is ACKED by the server (data unchanged, hasPendingWrites
    // flips to false). The squadId flip below waits for that ack.
    const unsubSelf = onSnapshot(doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', user.uid), { includeMetadataChanges: true }, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            const currentWeek = getWeekId();
            
            // Sync Squad ID from the doc — INCLUDING null (skipping null left
            // stale state after Leave Squad, so re-joining the same squad never
            // showed the lobby). And ONLY once the server has committed the
            // write: the local echo of a join arrives instantly, before the
            // membership the rules check exists server-side, so listeners that
            // attach on it get permission-denied and sit in retry backoff —
            // the "chat takes 10s to appear" complaint. Post-ack, they attach
            // clean and populate immediately.
            if (!docSnap.metadata.hasPendingWrites) {
                const next = data.squadId || null;
                // Left/removed remotely: also drop our member doc so a stale
                // pin/roster entry doesn't linger in the old squad.
                if (prevSquadRef.current && !next) {
                    deleteDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'squads', prevSquadRef.current, 'members', user.uid)).catch(() => {});
                }
                prevSquadRef.current = next;
                setSquadId(next);
            }
            
            // Only load stats if it's the same week (New Week = Reset Stats)
            if (data.weekId === currentWeek) {
                if (!isDataLoaded) {
                    // Hydrate a fresh open from the server's weekly totals — but
                    // NEVER merge them into a LIVE session (prev.startTime set).
                    // A resumed sober night would otherwise inherit the week's
                    // drink count via max(), and the session finalize would save
                    // that as last night's "debt" (Rap Sheet showed drinks the
                    // user never logged that night). During tracking, local
                    // session counts are authoritative.
                    setStats(prev => {
                        if (prev.startTime) return prev; // live session — don't pollute
                        return {
                            ...prev,
                            drinks: Math.max(prev.drinks || 0, data.drinks || 0),
                            waters: Math.max(prev.waters || 0, data.waters || 0),
                            steps:  Math.max(prev.steps  || 0, data.steps  || 0),
                            // Both sentinels mean "unset" — "PICK POISON" is the
                            // pre-1.4.3 value that may persist in old local data.
                            drinkOfChoice: (prev.drinkOfChoice && prev.drinkOfChoice !== "PICK POISON" && prev.drinkOfChoice !== "CHOOSE DRINK")
                                ? prev.drinkOfChoice
                                : (data.drinkOfChoice || "CHOOSE DRINK")
                        };
                    });
                }
            }
            setIsDataLoaded(true);
        } else {
            // New user or no data yet
            setIsDataLoaded(true);
        }
    });
    return () => unsubSelf();
  }, [user, db, APP_ID, isDataLoaded, setStats]);

  // --- 3. DATA SYNC (Save to Cloud) ---
  useEffect(() => {
    if (!user || !email || !isDataLoaded) return;
    
    const sync = async () => {
      try {
        const current = statsRef.current; // always the latest stats, via ref
        const smartScore = calculateSmartScore(current);
        const name = username || email.split('@')[0];

        // Leaderboard-public doc — world-readable, so it carries NO location.
        await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', user.uid), {
          name,
          countryCode: getTeamCountry() || null, // the user's chosen team flag (defaults to device country)
          drinks: current.drinks,
          waters: current.waters,
          steps: current.steps,
          drinkOfChoice: current.drinkOfChoice,
          score: smartScore,
          lastActive: serverTimestamp(),
          // NOT squadId: membership is written by join/leave (and cleared
          // remotely by moderation/seed tooling). A resumed app's sync loop
          // used to write its stale local squadId back before the server's
          // "you left" arrived, resurrecting membership. Local state follows
          // the server via the user-doc listener above.
          weekId: getWeekId()
        }, { merge: true });

        // Live location goes ONLY to the squad-scoped members subcollection, and
        // only while you're in a squad and have a fix. A solo user's precise
        // location never leaves the device.
        if (squadId && Array.isArray(current.location)) {
          await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'squads', squadId, 'members', user.uid), {
            name,
            location: current.location,
            squadId,
            lastActive: serverTimestamp(),
          }, { merge: true });
        }
      } catch (e) { console.error("Sync error", e); }
    };

    // OPTIMIZATION: Slowed down to 15 seconds to save costs/battery
    // Was 5000 (12 writes/min) -> Now 15000 (4 writes/min)
    // NOTE: `stats` intentionally NOT in the dep array — see statsRef above.
    const interval = setInterval(sync, 15000);
    return () => clearInterval(interval);
  }, [user, email, username, squadId, isDataLoaded, db, APP_ID]);

  // --- 4. SQUAD LISTENERS ---
  useEffect(() => {
      if (!squadId) { setSquadMembers([]); return; }

      // Read live locations from the squad-scoped members subcollection (the
      // access-controlled path) — NOT the world-readable users collection.
      const membersRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'squads', squadId, 'members');

      // resilientSnapshot: right after a join, the membership rules can reject
      // this read until the user-doc write commits server-side — retry instead
      // of dying and leaving the squad map empty until restart.
      const unsubSquad = resilientSnapshot(membersRef, (snapshot) => {
          const now = Date.now();
          const members = snapshot.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(m => m.id !== user?.uid) // exclude self
            // drop members who left / went offline (no fresh write in 30 min)
            .filter(m => !m.lastActive || (now - m.lastActive.toMillis()) < 30 * 60 * 1000);
          setSquadMembers(members);
      }, { label: 'squad-pins' });
      return () => unsubSquad();
  }, [squadId, user, db, APP_ID]);

  // --- 5. CHAT NOTIFICATIONS ---
  useEffect(() => {
    if (!squadId || !user || showChat) {
        if (showChat) setHasUnreadMessages(false);
        return;
    }
    
    const msgRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'squads', squadId, 'messages');
    const q = query(msgRef, orderBy('timestamp', 'desc'), limit(1));
    let isFirstLoad = true;
    
    const unsubChat = resilientSnapshot(q, (snapshot) => {
        if (isFirstLoad) { isFirstLoad = false; return; }
        
        snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
                const data = change.doc.data();
                if (data.senderId !== user.uid) {
                    // Avoid double-notifying: in-app toast when foreground, OS
                    // banner only when backgrounded/locked (where there's no toast).
                    const isForeground = typeof document !== 'undefined' && document.visibilityState === 'visible';
                    if (isForeground) {
                        if (onNotify) onNotify('info', `New message from ${data.senderName || 'Squad'}`);
                    } else {
                        fireNotification({
                            title: `${data.senderName || 'Squad'} 💬`,
                            body: data.text || (data.imageUrl ? '📷 Photo' : 'New squad message'),
                        });
                    }
                    setHasUnreadMessages(true);
                }
            }
        });
    });
    return () => unsubChat();
  }, [squadId, user, showChat, db, APP_ID, onNotify]);

  return {
    user,
    email, setEmail,
    username, setUsername,
    squadId,
    squadMembers,
    hasUnreadMessages, setHasUnreadMessages,
    isDataLoaded
  };
};

export default useBenderSession;
